import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePromoContext } from '../../hooks/usePromoContext';
import { selectPromosForSurface, type PromoSurface } from '../../config/promoContent';
import PromoCard, { type PromoVariant } from './PromoCard';

const DISMISS_KEY_PREFIX = 'safaqat24.promo.dismissed.';

interface PromoRailProps {
  surface: PromoSurface;
  /** Cap the number of cards shown on this surface. */
  max?: number;
  variant?: PromoVariant;
  /**
   * Allow per-card dismissal (persisted). Defaults to true for the compact
   * 'rail' variant and false for the home 'band'.
   */
  dismissible?: boolean;
  className?: string;
}

function readDismissed(id: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(DISMISS_KEY_PREFIX + id) === '1';
  } catch {
    return false;
  }
}

/**
 * Drops the right self-advertising cards onto a surface. Selection + targeting
 * is pure (selectPromosForSurface); this component layers in browser-persisted
 * dismissal and layout (vertical stack for rails, responsive grid for bands).
 */
export default function PromoRail({
  surface,
  max = 2,
  variant = 'rail',
  dismissible,
  className,
}: PromoRailProps) {
  const ctx = usePromoContext();
  const canDismiss = dismissible ?? variant === 'rail';

  const candidates = useMemo(() => selectPromosForSurface(surface, ctx), [surface, ctx]);

  // Track dismissed ids so a dismiss re-renders without a reload.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!canDismiss) return;
    const next = new Set<string>();
    candidates.forEach((entry) => {
      if (readDismissed(entry.id)) next.add(entry.id);
    });
    setDismissed(next);
  }, [candidates, canDismiss]);

  const handleDismiss = useCallback((id: string) => {
    try {
      window.localStorage.setItem(DISMISS_KEY_PREFIX + id, '1');
    } catch {
      /* ignore storage failures (private mode, quota) */
    }
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const visible = candidates.filter((entry) => !dismissed.has(entry.id)).slice(0, max);

  if (visible.length === 0) return null;

  const layout =
    variant === 'band'
      ? 'grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'
      : 'flex flex-col gap-4';

  return (
    <div className={[layout, className].filter(Boolean).join(' ')}>
      {visible.map((entry) => (
        <PromoCard
          key={entry.id}
          entry={entry}
          ctx={ctx}
          variant={variant}
          dismissible={canDismiss}
          onDismiss={handleDismiss}
        />
      ))}
    </div>
  );
}
